import React, { useContext, useRef } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { DatasheetIcon } from './DatasheetIcon';
import { RohsIcon } from './RohsIcon';
import queryStringAtom from '../../recoils/useQueryString/queryStringAtom';
import './PartNumberCellRenderer.scss';
import reverseResultAtom from '../../recoils/useReverseResult/reverseResultAtom';
import { createUrlSlug, getSupplierNameCorePart, getLangSite } from '../../ultils';
import { useTranslation } from 'react-i18next';
import selectedCheckboxsAtom from '../../recoils/useSelectedCheckboxsReverseSearch/selectedCheckboxsAtom';
import utagObjectAtom from '../../recoils/useUtagObject/utagObjectAtom';
import { callFunctionDkView, callGTMAnalytics } from '../../helper/analyticsTag';
import { pinnedSelectionState } from '../../recoils/usePinnedSelection/isPinnedSelectionAtom';
import { TabContext } from '../../context/tabContext';

export const PartNumberCellRenderer: React.FC = (props: any) => {
  const [queryString] = useRecoilState(queryStringAtom);
  const [result] = useRecoilState(reverseResultAtom);
  const [listCheckbox] = useRecoilState(selectedCheckboxsAtom);
  const [utagObject] = useRecoilState(utagObjectAtom);
  const event_state_ref = useRef<string[]>([]);
  const { t } = useTranslation();
  const { data, node } = props;
  const dataSupplierName = data?.productVariations
    ? getSupplierNameCorePart(data?.productVariations)
    : data?.vendor?.value;
  const isPinned = node?.rowPinned === 'top';
  const setPinnedSelected = useSetRecoilState(pinnedSelectionState);
  const { tabId } = useContext(TabContext);
  const isNonDGK = isPinned && node.data.type === "Non Digikey";

  // set status if source part is selected
  const handleCheckboxChange = (e: any) => {
    const isChecked = e.target.checked;
    const pinnedDiv = document.getElementById('pinnedTopRow');

    if (isPinned) {
      if (isChecked) {
        pinnedDiv?.classList.add('ag-checked');
      } else {
        pinnedDiv?.classList.remove('ag-checked');
      }
      setPinnedSelected(isChecked);
    }
  };


  //Handling render box cell in first row for reverse table
  if (data.typeColumn && data.typeColumn === 'first-row') {
    return (
      <h4 id="first-row-title">{t('xref-find-parts-matching-selected-parameters')}</h4>
    );
  }

  //Handling render box cell in second row for reverse table
  if (data.typeColumn && data.typeColumn === 'second-row') {
    const langSiteObj = getLangSite('en');
    const lang = langSiteObj.lang;
    //set style ag-cell-wrapper
    if (props) {
      props.eParentOfValue?.__agComponent?.eCellValue?.setAttribute(
        'style',
        'width:100%;'
      );
    }
    const handleButton = async (queryString: string) => {
      const utagUpdate = {
        ...utagObject,
        ...{
          ref_page_event: 'View Similar Parts',
          event_state: event_state_ref.current.join(',')
        }
      };
      callGTMAnalytics(utagUpdate);
      callFunctionDkView(utagUpdate);
      window.open(
        `https://${window.location.host}/${lang}/products/category/${createUrlSlug(
          props.data.category.name,
          true
        )}/${props.data.category.categoryId}?s=${queryString}`
      );
    };

    //convert column field array to column header name array
    if (listCheckbox.length > 0) {
      let event_state_array: string[] = [];
      listCheckbox.forEach((iCheckbox: string) => {
        const result = props.allColumns.find((iCol: any) => {
          return iCol.field === iCheckbox;
        });
        if (result)
          event_state_array = [...event_state_array, result.headerName];
      });
      event_state_ref.current = event_state_array;
    }

    return (
      <div className="button-wrapper">
        <button
          id="view-all-btn"
          data-testid="view-all-btn"
          className="dk-btn__primary btnViewMatching"
          onClick={() => handleButton(queryString)}
          {...{
            ref_page_event: 'View Similar Parts',
            event_state: `${event_state_ref.current.join(',')}`
          }}
        >
          {t('xref-view-matching-parts')}
        </button>

        {listCheckbox.length > 0 && result && (
          <span className="matching-result">
            {result} {t('xref-results')}
          </span>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={tabId !== 'tabReverseLookup' ? 'checkbox-container' : 'checkbox-container-reverse'}>
          {(tabId !== 'tabReverseLookup' && isPinned && !isNonDGK) && (
            <div id='pinnedTopRow' className="ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper">
              <input
                type="checkbox"
                className="ag-checkbox-input"
                checked={isPinned ? props.pinnedSelected : node.isSelected()}
                onChange={handleCheckboxChange}
              />
            </div>
          )}
        <div className="container part-number-container">

          <div className="icon-section">
            {data.primaryDatasheetUrl ? (
              <DatasheetIcon primaryDatasheetUrl={data.primaryDatasheetUrl} />
            ) : (
              ''
            )}
            <RohsIcon rohsStatus={data.rohsStatus} />
          </div>
          <div className="left-content">
            <img
              alt="product_image"
              className="product-image"
              src={
                data.primaryPhotoUrl
                  ? data.primaryPhotoUrl
                  : 'https://media.digikey.com/Photos/NoPhoto/NoPhoto_sml.jpg'
              }
            />
          </div>

          <div className="right-content">
            {data.url !== '' ? (
              <a
                className="right-content__mnf-part-number"
                href={data?.url}
                target="_blank"
                rel="noreferrer"
                onClick={() => callGTMAnalytics(utagObject, "Link to Part", [data])}
                cookie-tracking={
                  data &&
                    data.crossType &&
                    data.crossType?.crossName !== undefined
                    ? 'ref_page_event=Link to Part;ref_page_category=' +
                    data.crossType.crossName
                    : ''
                }
              >
                {data.manufacturerProductNumber}
              </a>
            ) : (
              <span className="right-content__mnf-part-number pe-none">
                {data.manufacturerProductNumber}
              </span>
            )}
            <span className="right-content__description">
              {data.catalogDescription}
            </span>
            <span className="right-content__vendor" >{dataSupplierName}</span>
          </div>
        </div>
      </div>
    </>
  );
};
